import { BoltIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ContentFull, ContentHeader } from "components/layout";
import { Badge } from "components/ui/badge";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SearchInput } from "components/ui/search-input";
import { Select } from "components/ui/select";
import { AdvancedSearchEntityType, WorkflowStatus } from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React from "react";
import { Link } from "react-router-dom";
import { useBridgeTypes, useWorkflowEntityTypes } from "store/selectors/bridge";
import { useCrmMode } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { EditableWorkflow } from "../types";
import {
    WorkflowClientStatusesSummary,
    WorkflowTriggerSummary,
} from "../workflow-trigger-summary";
import { workflowListEventProperties } from "./builder-analytics";
import {
    filterWorkflows,
    presentEntityTypes,
    sortWorkflowsByRecentlyUpdated,
    workflowEntityTypeLabel,
} from "./builder-helper";
import { EntityTypeIcon } from "./entity-type-icon";
import { SequenceStrip } from "./sequence-strip";

interface WorkflowCardProps {
    workflow: EditableWorkflow;
}

const WorkflowCard: React.FC<WorkflowCardProps> = props => {
    const { workflow } = props;
    const { mode } = useCrmMode();

    return (
        <Link
            to={`/settings/workflow/${workflow.id}`}
            className="block rounded-lg border border-gray-200 bg-white p-4 hover:border-brand-300 hover:shadow-sm transition">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">
                            {workflow.name}
                        </span>
                        {workflow.status === WorkflowStatus.Published ? (
                            <Badge value="Active" type="success" />
                        ) : (
                            <Badge value="Draft" type="info" />
                        )}
                        <span className="text-xs text-gray-400">&middot;</span>
                        <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                            <EntityTypeIcon
                                attachTo={workflow.attachTo}
                                className="w-3.5 h-3.5"
                            />
                            {workflowEntityTypeLabel(workflow.attachTo, mode)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1 flex items-center gap-1 flex-wrap">
                        <BoltIcon className="w-3.5 h-3.5" />
                        <WorkflowTriggerSummary workflow={workflow} />
                        <WorkflowClientStatusesSummary workflow={workflow} />
                    </div>
                    <div className="mt-3">
                        <SequenceStrip workflow={workflow} />
                    </div>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-gray-300 shrink-0 mt-1" />
            </div>
        </Link>
    );
};

interface WorkflowCardListProps {
    workflows: EditableWorkflow[];
    refreshing: boolean;
    onCreate: () => void;
    createEnabled: boolean;
    searchText: string;
    onSearchTextChange: (searchText: string) => void;
    entityTypeFilter: AdvancedSearchEntityType | "all";
    onEntityTypeFilterChange: (
        entityTypeFilter: AdvancedSearchEntityType | "all",
    ) => void;
    statusFilter: WorkflowStatus | "all";
    onStatusFilterChange: (statusFilter: WorkflowStatus | "all") => void;
}

export const WorkflowCardList: React.FC<WorkflowCardListProps> = props => {
    const {
        workflows,
        refreshing,
        onCreate,
        createEnabled,
        searchText,
        onSearchTextChange,
        entityTypeFilter,
        onEntityTypeFilterChange,
        statusFilter,
        onStatusFilterChange,
    } = props;
    const nouns = useTaskNouns();
    const workflowEntityTypes = useWorkflowEntityTypes();
    const { status: bridgeTypesStatus } = useBridgeTypes();
    const { mode } = useCrmMode();
    const listViewReported = React.useRef(false);
    const countsSettled = !refreshing && bridgeTypesStatus === "ready";

    React.useEffect(() => {
        if (listViewReported.current || !countsSettled) {
            return;
        }
        listViewReported.current = true;
        handleEvent("workflow-builder-list-view", workflowListEventProperties(workflows));
    }, [countsSettled, workflows]);

    const handleEntityTypeFilterChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value as AdvancedSearchEntityType | "all";
            onEntityTypeFilterChange(value);
            handleEvent("workflow-builder-list-filter", {
                field: "entityType",
                value,
            });
        },
        [onEntityTypeFilterChange],
    );

    const handleStatusFilterChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value as WorkflowStatus | "all";
            onStatusFilterChange(value);
            handleEvent("workflow-builder-list-filter", { field: "status", value });
        },
        [onStatusFilterChange],
    );

    const filterableEntityTypes = presentEntityTypes(workflowEntityTypes, workflows);
    const filteredWorkflows = sortWorkflowsByRecentlyUpdated(
        filterWorkflows(workflows, entityTypeFilter, statusFilter, searchText),
    );

    return (
        <ContentFull>
            <div className="space-y-4">
                <div className="flex justify-between items-baseline [&_h2]:m-0">
                    <ContentHeader>Workflows</ContentHeader>
                    <PrimaryButton onClick={onCreate} disabled={!createEnabled}>
                        Create workflow
                    </PrimaryButton>
                </div>
                <p className="text-sm text-gray-500 max-w-2xl">
                    {`Automate important processes with Workflows. Automatically send follow-up emails, create follow-up ${nouns.plural}, and more.`}
                </p>
                {workflows.length === 0 ? (
                    <InlineBanner type="info">
                        No workflows have been created yet. Use the &quot;Create
                        workflow&quot; button above to get started.
                    </InlineBanner>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="max-w-sm flex-1 min-w-[12rem]">
                                <SearchInput
                                    aria-label="Search workflows"
                                    placeholder="Search workflows"
                                    value={searchText}
                                    onChange={e => onSearchTextChange(e.target.value)}
                                />
                            </div>
                            {filterableEntityTypes.length > 1 ? (
                                <Select
                                    value={entityTypeFilter}
                                    onChange={handleEntityTypeFilterChange}>
                                    <option value="all">All types</option>
                                    {filterableEntityTypes.map(et => (
                                        <option key={et} value={et}>
                                            {workflowEntityTypeLabel(et, mode)}
                                        </option>
                                    ))}
                                </Select>
                            ) : null}
                            <Select
                                value={statusFilter}
                                onChange={handleStatusFilterChange}>
                                <option value="all">All statuses</option>
                                <option value={WorkflowStatus.Published}>Active</option>
                                <option value={WorkflowStatus.Draft}>Draft</option>
                            </Select>
                        </div>
                        {filteredWorkflows.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                No workflows match your filters.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {filteredWorkflows.map(workflow => (
                                    <WorkflowCard key={workflow.id} workflow={workflow} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ContentFull>
    );
};
